/**
 * In-app tray / FCM data payload for shift & patrol notifications.
 * Backend sends `notification_category`, `notification_kind`, plus `title` and `body`
 * (same as tray copy) inside `data` when the app reads the notification from data.
 */

export type NotificationCategory = "patrol" | "shift";

export type NotificationKind =
  | "patrol_overdue"
  | "patrol_upcoming"
  | "shift_created"
  | "shift_updated"
  | "shift_cancelled"
  | "shift_deleted"
  | "shift_copied"
  | "shift_reassigned_away";

export type TrayTone = "alert" | "info" | "success";

export type TrayNotificationData = {
  notification_category?: string;
  notification_kind?: string;
  /** Legacy field; prefer `notification_kind`. */
  type?: string;
  title?: string;
  body?: string;
  [key: string]: unknown;
};

function normalizeKind(data?: TrayNotificationData | null): string | undefined {
  if (!data) return undefined;
  const k = data.notification_kind ?? data.type;
  return typeof k === "string" ? k : undefined;
}

/** Infer category from `notification_category` or from kind prefix / legacy type. */
export function resolveNotificationCategory(
  data?: TrayNotificationData | null
): NotificationCategory | undefined {
  const raw = data?.notification_category;
  if (raw === "patrol" || raw === "shift") return raw;

  const kind = normalizeKind(data);
  if (!kind) return undefined;
  if (kind.startsWith("patrol_")) return "patrol";
  if (kind.startsWith("shift_")) return "shift";
  return undefined;
}

export function resolveNotificationKind(
  data?: TrayNotificationData | null
): NotificationKind | undefined {
  const k = normalizeKind(data);
  if (!k) return undefined;
  const allowed: NotificationKind[] = [
    "patrol_overdue",
    "patrol_upcoming",
    "shift_created",
    "shift_updated",
    "shift_cancelled",
    "shift_deleted",
    "shift_copied",
    "shift_reassigned_away",
  ];
  return (allowed as string[]).includes(k) ? (k as NotificationKind) : undefined;
}

/**
 * Visual / semantic tone for tray styling.
 * - alert: urgent or destructive (red family)
 * - info: neutral updates (blue family)
 * - success: positive additions (green family)
 */
export function getTrayTone(kind: NotificationKind | undefined): TrayTone {
  if (!kind) return "info";
  switch (kind) {
    case "patrol_overdue":
    case "shift_cancelled":
    case "shift_deleted":
      return "alert";
    case "shift_created":
    case "shift_copied":
      return "success";
    default:
      return "info";
  }
}

export function getTrayToneClasses(tone: TrayTone): {
  card: string;
  title: string;
  body: string;
  action: string;
  close: string;
} {
  switch (tone) {
    case "alert":
      return {
        card: "border-red-200 bg-red-50 shadow-lg shadow-red-900/10",
        title: "text-red-950",
        body: "text-red-900/90",
        action: "text-red-800 hover:text-red-950 underline-offset-2 hover:underline font-semibold",
        close:
          "rounded-md p-1.5 text-red-800/80 hover:bg-red-100 hover:text-red-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400",
      };
    case "success":
      return {
        card: "border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-900/10",
        title: "text-emerald-950",
        body: "text-emerald-900/90",
        action:
          "text-emerald-800 hover:text-emerald-950 underline-offset-2 hover:underline font-semibold",
        close:
          "rounded-md p-1.5 text-emerald-800/80 hover:bg-emerald-100 hover:text-emerald-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400",
      };
    case "info":
    default:
      return {
        card: "border-blue-200 bg-blue-50 shadow-lg shadow-blue-900/10",
        title: "text-blue-950",
        body: "text-blue-900/90",
        action:
          "text-blue-800 hover:text-blue-950 underline-offset-2 hover:underline font-semibold",
        close:
          "rounded-md p-1.5 text-blue-800/80 hover:bg-blue-100 hover:text-blue-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400",
      };
  }
}
